/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Grid component to display all routes per client in Data of Dedicated Delivery page.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import getAxiosError from "../../../../app/api/axiosError";
import { getGeneralClientData } from "../../../../app/features/dedicatedDelivery/dashboardSlice";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import { useAppDispatch } from "../../../../app/features/store/store";
import { DdGeneralClientDetails } from "../../../../app/models/responseModels/DdGeneralClientDetails";
import Loader from "../../../../components/common/loader/Loader";
import { ClientGeneralProps } from "../../DdTypes";
import ClientContactGrid from "../../../clients/contact/ClientContact";

// component to redner general details of a client
const ClientGeneral = (generalArgs: ClientGeneralProps) => {
  // access to passed parameter
  const clientId = Number(generalArgs.clientId);
  const dispatch = useAppDispatch();

  // state to manage data for the page
  const [generalClientData, setGeneralClientData] =
    useState<DdGeneralClientDetails>();

  // flag to enable loader on the page
  const [fetchingData, setFetchigData] = useState<boolean>(true);

  // state to manage accordions on the page
  const [openClient, setOpenClient] = useState(true);
  const [openCcontact, setOpenCcontact] = useState(true);
  const [openFdp, setOpenFdp] = useState(true);

  // toggle accordion function
  const toggle = (func: Function, open: boolean) => {
    func(!open);
  };

  // change page title
  useEffect(() => {
    document.title = "Dedicated Delivery - General";
  }, []);

  // call to server for data of this page
  const getClientsData = useCallback(async () => {
    try {
      if (generalArgs.clientId !== 0) {
        const response = await dispatch(
          getGeneralClientData(generalArgs.clientId)
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message!, {
            toastId: "clientData",
          });
        }
        setGeneralClientData(response.data);
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientDataException",
      });
    } finally {
      setFetchigData(false);
    }
  }, [dispatch]);

  // incase flag is true make call to server to get data
  useEffect(() => {
    if (fetchingData) {
      getClientsData();
    }
  }, [fetchingData, getClientsData]);

  return (
    <>
      <Loader isLoading={fetchingData} />
      {/* Client details */}
      <Accordion elevation={2} expanded={openClient}>
        <AccordionSummary
          onClick={() => {
            toggle(setOpenClient, openClient);
          }}
          expandIcon={
            <IconButton
              onClick={() => {
                toggle(setOpenClient, openClient);
              }}
              sx={{ color: "white" }}
            >
              <ExpandMore sx={{ color: "white" }} />
            </IconButton>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "white",
            }}
          >
            <Typography>Client details:</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container marginTop={1} columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Client Name"
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                variant="filled"
                value={generalClientData?.client.clientName}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Client Id"
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                variant="filled"
                value={generalClientData?.client.id}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Total Stores Served"
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                variant="filled"
                value={generalClientData?.totalStoresServed}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Client Personal details */}
      <Accordion elevation={2} expanded={openCcontact}>
        <AccordionSummary
          onClick={() => {
            toggle(setOpenCcontact, openCcontact);
          }}
          expandIcon={
            <IconButton
              onClick={() => {
                toggle(setOpenCcontact, openCcontact);
              }}
              sx={{ color: "white" }}
            >
              <ExpandMore sx={{ color: "white" }} />
            </IconButton>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "white",
            }}
          >
            <Typography>Contact details:</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* <Grid container marginTop={1} columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Primary Contact Name"
                InputProps={{
                  readOnly: false,
                  autoComplete: "off",
                }}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Job Title"
                InputProps={{
                  readOnly: false,
                  autoComplete: "off",
                }}
                size="small"
                variant="outlined"
              />
            </Grid>{" "}
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Email"
                InputProps={{
                  readOnly: false,
                  autoComplete: "off",
                }}
                size="small"
                variant="outlined"
              />
            </Grid>{" "}
            <Grid item xs={12} sm={3} md={3} lg={3}>
              <TextField
                fullWidth
                id="standard-read-only-input"
                label="Phone"
                InputProps={{
                  readOnly: false,
                  autoComplete: "off",
                }}
                size="small"
                variant="outlined"
              />
            </Grid>
          </Grid> */}
          <ClientContactGrid
            data={generalClientData?.client.clientContacts || []}
          />
        </AccordionDetails>
      </Accordion>
      {/* FDPS */}
      {generalClientData?.stores.flatMap((e) => e.fdps) && (
        <Accordion elevation={2} expanded={openFdp}>
          <AccordionSummary
            onClick={() => {
              toggle(setOpenFdp, openFdp);
            }}
            expandIcon={
              <IconButton
                onClick={() => {
                  toggle(setOpenFdp, openFdp);
                }}
                sx={{ color: "white" }}
              >
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              minHeight: "36px",
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
              "& .MuiAccordionSummary-content.Mui-expanded": {
                margin: 0,
              },

              "&.Mui-expanded": {
                minHeight: "36px",
              },
              backgroundColor: "primary.main",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "white",
              }}
            >
              <Typography>Associated FDP(s):</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {generalClientData?.stores
              .flatMap((e) => e.fdps)
              .map((element, idx) => {
                return (
                  <Grid
                    container
                    marginTop={1}
                    rowSpacing={2}
                    columnSpacing={2}
                    key={idx}
                  >
                    <Grid item xs={12} sm={4} md={4} lg={4}>
                      <TextField
                        fullWidth
                        id="standard-read-only-input"
                        label="Fdp Id"
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                        variant="filled"
                        defaultValue={element.id}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={4}>
                      <TextField
                        fullWidth
                        id="standard-read-only-input"
                        label="Fdp Name"
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                        variant="filled"
                        defaultValue={element.companyName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4} lg={4}>
                      <TextField
                        fullWidth
                        id="standard-read-only-input"
                        label="Associated Stores"
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                        variant="filled"
                        defaultValue={generalClientData.stores
                          .filter(
                            (e) =>
                              e.fdps.filter((e) => e.id === element.id).length
                          )
                          .map((e) => e.clientStoreId)
                          .join(", ")}
                      />
                    </Grid>
                  </Grid>
                );
              })}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
export default ClientGeneral;
